import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { assetData, type TripSegment } from "@/api/client";

/** What happened to one asset over a window: Running / Stopped, in order.
 *
 *  Dattatray asked for the WheelsEye shape — *"Can we build a similar UI for
 *  Our device in Asset data -> Live route? maybe we can select a time window
 *  can show what happedn with that assst?"* — so this is their left-hand
 *  column: alternating segments, each with its duration, its distance and
 *  when it ran.
 *
 *  IT REUSES THIS PAGE'S WINDOW. History already owns From / To and a Load
 *  button, so this takes the loaded range as props rather than growing a
 *  third date picker beside the two that exist. A second copy of a control
 *  the page already has is the review failure CLAUDE.md names by name.
 *
 *  WHAT IT DELIBERATELY DOES NOT SHOW: an address. WheelsEye prints *"130 m
 *  from Hazarat Sayyed Mustafa Qadri Dargah, …, 581205, INDIA"* against every
 *  segment, and that line — not the map, not the polyline — is the part that
 *  costs money per call. Dattatray's constraint is no map-API spend, so the
 *  coordinates are shown as coordinates until a local reverse-geocode exists.
 *  Showing a guessed address would be worse than showing none.
 *
 *  THREE KINDS, NOT TWO, and the third is the one that keeps this honest.
 *  `no_data` is a stretch Orbit has no evidence for — the device was off, or
 *  the link was down. It is drawn as its own row because folding it into a
 *  neighbour would state as fact something nobody observed: a 659-minute
 *  silence on the bench board would otherwise have read as a 659-minute trip.
 */

const KIND: Record<TripSegment["kind"],
                   { label: string; colour: string; mark: string }> = {
  running: { label: "Running", colour: "var(--ok)", mark: "▲" },
  stopped: { label: "Stopped", colour: "var(--bad)", mark: "■" },
  no_data: { label: "No data", colour: "var(--t-dim)", mark: "⋯" },
};

function dur(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h && m) return `${h} hr ${m} min`;
  if (h) return `${h} hr`;
  return `${m} min`;
}

function km(metres: number): string {
  return `${(metres / 1000).toFixed(1)} Km`;
}

/** "24 October (08:54 PM - 09:27 PM)", WheelsEye's format and Dattatray's ask.
 *
 *  WHAT WENT, and why each one was noise:
 *    * SECONDS AND MILLISECONDS. `localStamp` renders
 *      "2026-10-24 10:01:40.000", and nobody reads a trip list to the
 *      millisecond -- *"Time can be 1 min approximate"*.
 *    * THE DATE, TWICE. A segment almost always starts and ends on one day,
 *      and printing it at both ends spends a third of the line restating it.
 *
 *  IT COMES BACK WHEN THE SEGMENT ACTUALLY CROSSES MIDNIGHT, which is the
 *  only case where dropping it would mislead: a stop from 11:50 PM to 12:30
 *  AM reads as 40 minutes backwards in time without the second date. Rare,
 *  and exactly when it matters.
 */
function whenRange(startIso: string, endIso: string): string {
  const a = new Date(startIso), b = new Date(endIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "";
  const day = (d: Date) =>
    d.toLocaleDateString(undefined, { day: "numeric", month: "long" });
  const time = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return a.toDateString() === b.toDateString()
    ? `${day(a)} (${time(a)} - ${time(b)})`
    : `${day(a)} ${time(a)} - ${day(b)} ${time(b)}`;
}

/** The two distances, side by side, and the gap between them named.
 *
 *  NEVER RECONCILED INTO ONE NUMBER. The odometer is the device's own count;
 *  the GPS figure is summed position hops. They fail in opposite directions
 *  and neither can check itself, so picking a winner here would hide exactly
 *  the signal the pair exists to produce:
 *
 *    odometer moves, GPS flat  -> no fix (tunnel, canyon) or a stuck receiver
 *    GPS moves, odometer flat  -> the odometer is not wired, or its writes
 *                                 are not landing (a shipped firmware bug)
 *
 *  `odo_m === null` prints "—", never "0.0 Km": 0 claims the vehicle did not
 *  move, and the honest answer is that Orbit cannot tell.
 */
function Distance({ seg }: { seg: TripSegment }) {
  const gps = km(seg.gps_m);
  const odo = seg.odo_m === null ? null : km(seg.odo_m);
  //: THE BEST GPS FIGURE AVAILABLE is what the odometer is compared against:
  //: the device's own when it reports one, else Orbit's. Comparing against
  //: Orbit's weaker number when a better one is present would raise a flag
  //: about Orbit's drift rather than about the vehicle.
  const bestGps = seg.gps_odo_m !== null ? seg.gps_odo_m : seg.gps_m;
  //: MY FIRST VERSION REQUIRED `gps_m > 0` AND SO EXCLUDED THE STRONGEST
  //: CASE. On the bench -- an emulated odometer, a stationary board -- the
  //: segment reads "odometer 9.2 Km, device GPS 0.0 Km, Orbit GPS 0.0 Km",
  //: the most extreme disagreement there is, and the warning stayed silent
  //: because one side was zero. Found by rendering real data, not by a test.
  //:
  //: Flagged when they differ by more than a fifth, OR when either is zero
  //: while the other has covered real ground. GPS samples a curve as a chord
  //: and undercounts a little, so a small gap is expected and marking it
  //: would train people to ignore the mark that matters.
  const ZERO_IS_NEWS_M = 200;
  const hi = Math.max(seg.odo_m ?? 0, bestGps);
  const far = seg.odo_m !== null && hi > ZERO_IS_NEWS_M
    && Math.abs(seg.odo_m - bestGps) / hi > 0.2;
  //: WHICH WAY ROUND, because the two mean completely different things and
  //: "sources disagree" alone leaves an operator to work that out.
  const why = seg.odo_m === null ? ""
    : bestGps < seg.odo_m
      ? "The odometer reports movement that GPS denies: no fix (a tunnel, a "
        + "basement), a stuck receiver -- or, on a bench, an emulated "
        + "odometer against a stationary board."
      : "GPS reports movement the odometer denies: the odometer may not be "
        + "wired, or its writes may not be reaching flash.";
  //: THE DEVICE'S OWN GPS ODOMETER LEADS WHEN IT IS THERE. It is computed
  //: on-device at 1 Hz behind gates Orbit cannot reproduce -- a 3D fix, >= 6
  //: satellites, HDOP <= 2.0, a Doppler check and a frozen-solution veto --
  //: and the measurement that settled it is on the bench board: with a live
  //: fix and the CAN bus claiming 45 km/h, it correctly added ZERO while
  //: Orbit's own figure still drifted. Absent on builds below app 61, and
  //: absent is not zero.
  const devGps = seg.gps_odo_m === null ? null : km(seg.gps_odo_m);
  return (
    <div style={{ fontSize: 12 }}>
      {devGps !== null && (
        <span style={{ marginRight: 10 }}>
          Covered <b>{devGps}</b>{" "}
          <span className="cap" title={
            "The device's own GPS odometer, computed at 1 Hz behind fix-"
            + "quality and Doppler gates. The most trustworthy of the three."}
            style={{ cursor: "help" }}>device GPS</span>
        </span>
      )}
      <span>{devGps === null ? "Covered " : ""}<b>{odo ?? "—"}</b>{" "}
        <span className="cap">odometer</span></span>
      <span style={{ marginLeft: 10 }}>
        <b>{gps}</b>{" "}
        <span className="cap" title={
          "Orbit's own sum of position hops. The weakest of the three: at a "
          + "10-30 s sample interval a bad fix looks like ordinary driving, "
          + "measured at 7.6 km of drift over 18 days on a stationary board."}
          style={{ cursor: "help" }}>Orbit GPS</span>
      </span>
      {far && (
        <span title={why}
          style={{ marginLeft: 8, color: "var(--warn)", fontWeight: 700,
                   cursor: "help" }}>
          {/* THE SYMBOL ALONE, at Dattatray's request. "sources disagree"
              spent a third of a narrow line saying what ⚠ already says, and
              the hover carries the part that actually helps -- which of the
              two faults this is. */}
          ⚠
        </span>
      )}
    </div>
  );
}

/** WHAT COUNTS AS A STOP, and it is a different question in a city.
 *
 *  Dattatray: *"Wheels eyes routes are defined for highway movement. Ours is
 *  mostly city rides."* That is not a detail -- it inverts the threshold. On
 *  a highway a 3-minute halt is an event (a toll, a meal) and almost nothing
 *  else stops you for that long. In city traffic 3 minutes is a signal, a
 *  level crossing or a queue, and a fleet of 2-wheelers doing deliveries or
 *  swaps has real stops that are SHORTER than the noise a highway filter is
 *  built to remove.
 *
 *  SO IT IS A CONTROL, NOT A CONSTANT. Nobody has the right number yet and
 *  nobody will have it from first principles -- it comes from looking at real
 *  rides and seeing which choice makes the list read like the day. The server
 *  has always taken it as a parameter (30 s to 1 h) and echoes back the value
 *  that produced the segments; this exposes it so the answer can be found by
 *  moving it rather than by redeploying.
 */
const STOP_CHOICES: { s: number; label: string }[] = [
  { s: 60, label: "1 min" },
  { s: 120, label: "2 min" },
  { s: 180, label: "3 min" },
  { s: 300, label: "5 min" },
  { s: 600, label: "10 min" },
];

export function TripList({ imei, from, to }: {
  imei: string; from: string; to: string;
}) {
  //: 2 min, not WheelsEye's ~3: city rides, per above. Deliberately a
  //: starting point to be moved, not a decision.
  const [minStop, setMinStop] = useState(120);
  const q = useQuery({
    queryKey: ["asset-data", "trips", imei, from, to, minStop],
    queryFn: () => assetData.trips(imei, { from, to,
                                           min_stop_s: String(minStop) }),
    enabled: Boolean(imei),
  });

  if (q.isLoading) {
    return <div className="panel panel-pad cap">reading the window…</div>;
  }
  if (q.isError) {
    return (
      <div className="panel panel-pad" style={{ color: "var(--bad)", fontSize: 12 }}>
        {String((q.error as Error)?.message ?? "that did not work")}
      </div>
    );
  }
  const segs = q.data?.segments ?? [];
  const sum = q.data?.summary;
  if (segs.length === 0) {
    return (
      <div className="panel panel-pad cap">
        No telemetry from this device in that window.
      </div>
    );
  }

  return (
    <div className="panel panel-pad">
      <div style={{ display: "flex", alignItems: "center", gap: 8,
                    justifyContent: "space-between", marginBottom: 10 }}>
        <span className="lw-vc-sk">ROUTE DETAILS</span>
        {/* SMALL AND OUT OF THE WAY. It is a tuning knob for finding the
            right value on real city rides, not a decision an operator makes
            every visit. */}
        <label style={{ fontSize: 11, color: "var(--t-dim)", display: "flex",
                        alignItems: "center", gap: 5 }}
          title="A halt shorter than this is traffic, not a stop. City rides
                 need a lower value than highway running: three minutes on a
                 highway is an event, in traffic it is a signal.">
          stop ≥
          <select className="inp" value={minStop} style={{ padding: "1px 4px",
                   fontSize: 11, height: 22, width: 68 }}
            onChange={(e) => setMinStop(Number(e.target.value))}>
            {STOP_CHOICES.map((c) => (
              <option key={c.s} value={c.s}>{c.label}</option>
            ))}
          </select>
        </label>
      </div>

      {sum && (
        <div style={{ fontSize: 12, color: "var(--t-mute)", marginBottom: 12 }}>
          <b style={{ color: "var(--t-bright)" }}>{sum.trips}</b> trip
          {sum.trips === 1 ? "" : "s"},{" "}
          <b style={{ color: "var(--t-bright)" }}>{sum.stops}</b> stop
          {sum.stops === 1 ? "" : "s"} · moving {dur(sum.running_s)} · stopped{" "}
          {dur(sum.stopped_s)}
          {/* THE DENOMINATOR FOR EVERYTHING ABOVE IT. Every figure on this
              panel describes only the part of the window the device actually
              reported; without this an operator reads "4 trips" as the whole
              day when half the day is silence. */}
          {sum.covered_pct < 99.5 && (
            <span style={{ color: "var(--warn)" }}>
              {" "}· only <b>{sum.covered_pct}%</b> of this window was reported
            </span>
          )}
        </div>
      )}

      {q.data?.truncated && (
        <div style={{ fontSize: 11.5, color: "var(--warn)", marginBottom: 10 }}>
          That window holds more telemetry than one request returns, so these
          segments cover only its start. Narrow the window.
        </div>
      )}

      <div style={{ display: "grid", gap: 0 }}>
        {/* NEWEST FIRST, at Dattatray's request and as WheelsEye does it:
            the segment you open this panel to read is the one that just
            happened, and making someone scroll past a whole day to reach it
            is the wrong way round.

            DISPLAY ONLY. The server returns segments oldest-first and must:
            segmentation walks forward in time -- the gap rule, the merge of
            short stops, the departure-leg seed all depend on that order --
            so this reverses a COPY at the edge rather than asking for a
            different order. `toReversed` is avoided for the same reason:
            `segs` is query-cache data and must not be mutated. */}
        {[...segs].reverse().map((seg, i) => {
          const k = KIND[seg.kind];
          return (
            <div key={`${seg.start}-${i}`} className="ad-trip-row">
              {/* THE DOTTED SPINE, which Dattatray singled out: *"they ahve
                  very good dotted line left side of their route details"*.
                  It is not decoration -- it is what makes a column of
                  segments read as ONE JOURNEY in order rather than as a
                  stack of unrelated cards, which matters more for city
                  riding where there are many short legs.

                  Drawn by `.ad-trip-mark::before` so the line runs BEHIND
                  the markers and is clipped at the first and last rows; the
                  glyph carries the panel's own background so the line
                  appears to pass behind it. */}
              <span className="ad-trip-mark" style={{ color: k.colour }}>
                {k.mark}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ marginBottom: 3 }}>
                  <b style={{ color: k.colour, fontSize: 13 }}>{k.label}</b>
                  <span className="chip" style={{ marginLeft: 8, fontSize: 11 }}>
                    {dur(seg.seconds)}
                  </span>
                </div>
                {/* A PLACE ON A STOP, A DISTANCE ON A RUN -- WheelsEye's
                    split, and it is the right one. A moving segment has no
                    single place to name, so it gets what it covered; a stop
                    happened somewhere, and that is the whole question an
                    operator has about it.

                    It also makes the address AFFORDABLE: geocoding only
                    stops, not every segment, is what turns the one line that
                    costs per call into a few lookups a day per vehicle. */}
                {seg.kind === "running" && <Distance seg={seg} />}
                {seg.kind === "stopped" && seg.end_lat !== null && (
                  seg.place
                    ? (
                      <div style={{ fontSize: 12, color: "var(--t-mute)" }}
                        title={`${seg.end_lat.toFixed(5)}, ${seg.end_lng?.toFixed(5)}`}>
                        {seg.place}
                      </div>
                    )
                    : (
                      /* COORDINATES ARE THE FALLBACK, NOT THE FAILURE. The
                         cell is not cached yet and this request's lookup
                         budget was spent, so it resolves on a later visit.
                         Showing the numbers beats a blank line or a spinner
                         that never ends. */
                      <div style={{ fontSize: 12, color: "var(--t-dim)" }}
                        title="No place name for this point yet. It resolves
                               on a later visit: lookups are budgeted per
                               request so a cold window is never slow.">
                        {seg.end_lat.toFixed(5)}, {seg.end_lng?.toFixed(5)}
                      </div>
                    )
                )}
                <div style={{ fontSize: 11.5, color: "var(--t-dim)", marginTop: 3 }}>
                  {whenRange(seg.start, seg.end)}
                </div>
                {/* WHAT WAS CLAMPED OR DISCARDED, in words. A number this
                    panel quietly corrected is a number nobody can question. */}
                {seg.notes.map((n) => (
                  <div key={n} style={{ fontSize: 11, color: "var(--warn)",
                                        marginTop: 2 }}>{n}</div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
